import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Block } from '../classes/Block';
import {
  VideoSettings,
  RenderFormat,
  VIDEO_FORMATS,
  isVideoFormat,
} from './VideoSettings';
import { t } from '@lingui/core/macro';
import { useProject } from '../contexts/ProjectContext';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import { ApiBlock, ApiCardGroup, ApiPayload } from '../utils/apiTypes';
import { getLabelValueExchangedRowValues } from '../utils/labelValueExchange';
import { Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { exportToFormat, EXPORT_CONFIGS, ExportFormat } from '../utils/exporterUtils';
import { CardGroup } from '../utils/cardGroups';

interface UnifiedRenderProps {
  columnDefs: any[];
  sheetName: string;
  blocks: Block[];
  isEnabled: boolean;
  videoSettings: VideoSettings;
  selectedFormat: RenderFormat;
  onFormatChange: (format: RenderFormat) => void;
  cardGroups?: CardGroup[];
}

function getFormatDisplayName(format: RenderFormat): string {
  const displayNames: Record<RenderFormat, string> = {
    'H264': 'H264',
    'H265': 'H265',
    'ProRes_422_Proxy': 'ProRes 422 Proxy',
    'ProRes_422_LT': 'ProRes 422 LT',
    'ProRes_422': 'ProRes 422',
    'ProRes_422_HQ': 'ProRes 422 HQ',
    'ProRes_4444': 'ProRes 4444',
    'ProRes_4444_XQ': 'ProRes 4444 XQ',
    'PDF': 'PDF',
    'PNG': 'PNG',
    'TIFF': 'TIFF',
  };
  return displayNames[format] || format;
}

const createVideoRender = async (data: ApiPayload, projectId: string): Promise<{ status: string; render: { version: number } }> => {
  const response = await authFetch(`${getApiUrl()}/new-render?projectId=${projectId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    throw new Error('Failed to create render');
  }
  const result = await response.json();
  return result;
};

const UnifiedRender: React.FC<UnifiedRenderProps> = ({
  columnDefs,
  blocks,
  isEnabled,
  videoSettings,
  selectedFormat,
  onFormatChange,
  cardGroups = [],
}) => {
  const { selectedProject } = useProject();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [renderVersion, setRenderVersion] = useState<number | null>(null);
  const popupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCardsMode: boolean = videoSettings.mode === 'cards';
  const effectiveSelectedFormat: RenderFormat = isCardsMode && selectedFormat === 'PNG'
    ? 'PDF'
    : selectedFormat;

  const transformBlockToApiFormat = useCallback(
    (block: Block): ApiBlock => {
      const rows: string[][] = [];
      if (block.dataRows && block.dataRows.length > 0) {
        for (const dataRow of block.dataRows) {
          const rowValues: string[] = [];
          for (const colDef of columnDefs) {
            let value: string = String(dataRow[colDef.field] || '');

            // Special handling for logo blocks: ensure col2 is JSON encoded with scale key
            if (value && block.isLogoBlock && colDef.field === 'col2') {
              try {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _ = JSON.parse(value);
              } catch {
                const scaleValue: number = parseFloat(value);
                value = JSON.stringify({ scale: isNaN(scaleValue) ? 100 : scaleValue });
              }
            }
            rowValues.push(value);
          }

          const exchangedRowValues: string[] = getLabelValueExchangedRowValues(
            rowValues,
            block.exchangeLabelValue
          );
          rows.push(exchangedRowValues);
        }
      }
      return {
        name: block.name,
        properties: block.toRenderApiProperties(),
        rows: rows,
      };
    },
    [columnDefs]
  );

  const buildCardsPayload = useCallback((): ApiCardGroup[] => {
    const blockById = new Map(blocks.map(b => [b.id, b]));
    const assignedIds = new Set(cardGroups.flatMap(g => g.blockIds));
    const unassignedBlocks = blocks.filter(b => !assignedIds.has(b.id));

    const grouped: ApiCardGroup[] = cardGroups.map(group => ({
      card: group.blockIds
        .map(id => blockById.get(id))
        .filter((b): b is Block => b !== undefined)
        .map(b => transformBlockToApiFormat(b)),
    })).filter(g => g.card.length > 0);

    if (unassignedBlocks.length > 0) {
      grouped.push({ card: unassignedBlocks.map(b => transformBlockToApiFormat(b)) });
    }

    return grouped;
  }, [blocks, cardGroups, transformBlockToApiFormat]);

  const handleRender = async () => {
    if (!selectedProject) return;

    setIsLoading(true);
    try {
      if (isVideoFormat(effectiveSelectedFormat)) {
        const renderData: ApiBlock[] | ApiCardGroup[] = isCardsMode
          ? buildCardsPayload()
          : blocks.map(block => transformBlockToApiFormat(block));

        const payload: ApiPayload = {
          video_settings: {
            background_color: videoSettings.background_color,
            fps_num: videoSettings.fps_num,
            resolution: videoSettings.resolution,
            transparent: videoSettings.transparent,
            video_time: videoSettings.video_time,
            aspect_ratio: videoSettings.aspect_ratio,
            video_format: effectiveSelectedFormat,
            fonts: JSON.stringify(videoSettings.fonts),
            logos: JSON.stringify(videoSettings.logos),
            mode: videoSettings.mode,
            seconds_per_card: videoSettings.seconds_per_card,
          },
          data: renderData,
        };

        const response = await createVideoRender(payload, selectedProject.id);

        if (!response || !response.render || typeof response.render.version !== 'number') {
          throw new Error('Invalid response from server: missing version number');
        }

        const version: number = response.render.version;
        setRenderVersion(version);
        setShowPopup(true);

        if (popupTimeoutRef.current) {
          clearTimeout(popupTimeoutRef.current);
        }

        popupTimeoutRef.current = setTimeout(() => {
          setShowPopup(false);
          popupTimeoutRef.current = null;
        }, 5000);

      } else {
        // Image export - call /export-png endpoint with format parameter
        const formatLower = effectiveSelectedFormat.toLowerCase() as ExportFormat;
        const config = EXPORT_CONFIGS[formatLower];

        await exportToFormat({
          projectId: selectedProject.id,
          projectName: selectedProject.name ?? '',
          blocks,
          cardGroups,
          columnDefs,
          videoSettings,
          config,
          transparent: false,
        });
      }
    } catch (error) {
      console.error('Error during render:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to render: ${errorMessage}\nPlease try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormatChange = (e: SelectChangeEvent<string>) => {
    const nextFormat: RenderFormat = e.target.value as RenderFormat;
    if (isCardsMode && nextFormat === 'PNG') {
      onFormatChange('PDF');
      return;
    }
    onFormatChange(nextFormat);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
      }
    };
  }, []);

  const isVideo: boolean = isVideoFormat(effectiveSelectedFormat);
  const buttonText = isLoading
    ? (isVideo ? t`Rendering...` : t`Exporting...`)
    : t`Render`;

  return (
    <>
      <div className="unified-render" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Select
          value={effectiveSelectedFormat}
          onChange={handleFormatChange}
          style={{ width: '180px', height: '36px', fontSize: '14px' }}
          sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
        >
                    <MenuItem disabled style={{ opacity: 0.6, fontWeight: 600, fontSize: '12px', marginTop: '8px' }}>
            {t`Image Formats`}
          </MenuItem>
          <MenuItem value="PDF">PDF</MenuItem>
          <MenuItem value="PNG" disabled={isCardsMode}>PNG</MenuItem>
          <MenuItem value="TIFF">TIFF</MenuItem>

          {/* Video Formats */}
          <MenuItem disabled style={{ opacity: 0.6, fontWeight: 600, fontSize: '12px' }}>
            {t`Video Formats`}
          </MenuItem>
          {VIDEO_FORMATS.map((format) => (
            <MenuItem key={format} value={format}>
              {getFormatDisplayName(format)}
            </MenuItem>
          ))}
          
          {/* Divider */}
        </Select>

        <button
          onClick={handleRender}
          disabled={!isEnabled || isLoading}
          className="export-button"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            position: 'relative'
          }}
        >
          {isLoading && (
            <div
              className="loading-spinner"
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
          )}
          {buttonText}
        </button>

        {/* Tooltip Icon */}
        <div className="tooltip-wrapper" style={{ paddingLeft: '3px' }}>
          <span className="tooltip-icon">?</span>
          <span className="tooltip-text" style={{ paddingBottom: '2px' }}>
            {isVideo
              ? t`Remember to save the excel before downloading the video`
              : t`Export your credits as an image file`
            }
          </span>
        </div>
      </div>

      {/* Popup notification for video renders */}
      {showPopup && renderVersion !== null && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#319204',
            color: 'white',
            padding: '20px 40px',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: 'bold',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            zIndex: 9999,
            textAlign: 'center'
          }}
        >
          Render version {renderVersion} started
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default UnifiedRender;

